import React, { useState } from 'react';
import { EllipsisVertical, SquarePen, Eye, Share2, Download, Trash2, Star, Copy } from 'lucide-react';
import ShareModal from './ShareModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import ToastNotification, { Toast } from './ToastNotification';
import DocumentEditModal from './DocumentEditModal';

interface ActionMenuProps {
  documentId: string;
  documentName: string;
  onEdit?: () => void;
  onView?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  onFavorite?: () => void;
  onCopy?: () => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  documentId,
  documentName,
  onEdit,
  onView,
  onShare,
  onDownload,
  onDelete,
  onFavorite,
  onCopy
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...toast, id }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleEdit = () => {
    console.log('handleEdit called', { onEdit, documentName });
    if (onEdit) {
      onEdit();
    } else {
      console.log('Opening edit modal for:', documentName);
      setShowEditModal(true);
      addToast({
        type: 'info',
        title: 'Modification des métadonnées',
        message: `Ouverture de l'éditeur pour "${documentName}"...`
      });
    }
    setIsOpen(false);
  };

  // Debug: Log when component renders
  console.log('ActionMenu render:', { isOpen, showEditModal, documentName });

  const handleView = () => {
    if (onView) {
      onView();
    } else {
      addToast({
        type: 'info',
        title: 'Visualisation',
        message: `Ouverture de "${documentName}" en mode lecture...`
      });
    }
    setIsOpen(false);
  };

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      setShowShareModal(true);
    }
    setIsOpen(false);
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload();
    } else {
      try {
        // Simulation du téléchargement
        addToast({
          type: 'info',
          title: 'Téléchargement en cours...',
          message: `Préparation de "${documentName}"`
        });
        
        // Simulation d'un délai de téléchargement
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        addToast({
          type: 'success',
          title: 'Téléchargement terminé',
          message: `"${documentName}" a été téléchargé avec succès`
        });
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erreur de téléchargement',
          message: 'Impossible de télécharger le document'
        });
      }
    }
    setIsOpen(false);
  };

  const handleCopy = async () => {
    if (onCopy) {
      onCopy();
    } else {
      try {
        const link = `${window.location.origin}/documents/${documentId}`;
        await navigator.clipboard.writeText(link);
        addToast({
          type: 'success',
          title: 'Lien copié',
          message: 'Le lien du document a été copié dans le presse-papiers'
        });
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erreur de copie',
          message: 'Impossible de copier le lien'
        });
      }
    }
    setIsOpen(false);
  };

  const handleFavorite = () => {
    if (onFavorite) {
      onFavorite();
    } else {
      addToast({
        type: 'success',
        title: 'Ajouté aux favoris',
        message: `"${documentName}" a été ajouté à vos favoris`
      });
    }
    setIsOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    } else {
      setShowDeleteModal(true);
    }
    setIsOpen(false);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Simulation de la suppression
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      addToast({
        type: 'success',
        title: 'Document supprimé',
        message: `"${documentName}" a été supprimé définitivement`
      });
      
      setShowDeleteModal(false);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erreur de suppression',
        message: 'Impossible de supprimer le document'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveDocument = async (data: any) => {
    try {
      // Simulation de la sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      addToast({
        type: 'success',
        title: 'Métadonnées sauvegardées',
        message: `"${documentName}" a été mis à jour avec succès`
      });
      
      setShowEditModal(false);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erreur de sauvegarde',
        message: 'Impossible de sauvegarder les métadonnées'
      });
    }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('Button clicked, current isOpen:', isOpen);
          setIsOpen(!isOpen);
        }}
        className="p-1 text-eg-estompe hover:text-eg-gris transition-colors cursor-pointer"
        style={{ 
          backgroundColor: isOpen ? '#f0f9ff' : 'transparent',
          borderColor: isOpen ? '#0ea5e9' : undefined
        }}
        type="button"
      >
        <EllipsisVertical className="w-4 h-4" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute top-full right-0 mt-1 w-48 bg-eg-blanc border border-eg-bord rounded-md shadow-lg z-20">
            <div className="py-1">
              <button
                onClick={handleEdit}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <SquarePen className="w-4 h-4" />
                Modifier
              </button>
              
              <button
                onClick={handleView}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Voir
              </button>
              
              <button
                onClick={handleShare}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Partager
              </button>
              
              <button
                onClick={handleDownload}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Télécharger
              </button>
              
              <button
                onClick={handleCopy}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copier le lien
              </button>
              
              <button
                onClick={handleFavorite}
                className="w-full px-4 py-2 text-left text-sm text-eg-brun hover:bg-eg-papier flex items-center gap-2"
              >
                <Star className="w-4 h-4" />
                Ajouter aux favoris
              </button>
              
              <button
                onClick={handleDelete}
                className="w-full px-4 py-2 text-left text-sm text-eg-accent hover:bg-eg-accent/10 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        </>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        documentName={documentName}
        documentId={documentId}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        documentName={documentName}
        isDeleting={isDeleting}
      />

      {/* Document Edit Modal */}
      <DocumentEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveDocument}
        documentName={documentName}
        documentId={documentId}
      />

      {/* Toast Notifications */}
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
        />
      ))}
    </div>
  );
};

export default ActionMenu;
